"use client";

import { cloneElement, isValidElement } from "react";

import { cn } from "../../../../utils";
import type { TableRowType } from "../../Table.types";
import { TableCardMobileProps } from "./Table.types.mobile";
import { TableExpansionMobile } from "./TableExpansion.mobile";

export const TableCardMobile = <
  Row extends TableRowType,
  Group extends string = never,
>(
  props: TableCardMobileProps<Row, Group>,
) => {
  const {
    row,
    cardId,
    isOpen,
    classes,
    cardIndex,
    onCardClick,
    renderMobileCard,
    ...rest
  } = props;

  if (!renderMobileCard) return null;

  const cardClassName =
    typeof classes?.card === "string"
      ? classes.card
      : classes?.card?.({
          row,
          cardIndex,
          isCardOpen: isOpen,
        });

  const handleCardClick = () => {
    onCardClick?.({ row, cardId, cardIndex });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  const mobileCardElement = renderMobileCard({
    row,
    isOpen,
    cardId,
    cardIndex,
  });

  type MobileCardProps = {
    role?: string;
    tabIndex?: number;
    className?: string;
    onClick?: (e: React.MouseEvent) => void;
    onKeyDown?: (e: React.KeyboardEvent) => void;
  };

  const collapsedContent = isValidElement(mobileCardElement) ? (
    cloneElement(mobileCardElement as React.ReactElement<MobileCardProps>, {
      tabIndex: -1,
      role: "button",
      className: cn(
        "ui:cursor-pointer ui:p-3",
        (mobileCardElement as React.ReactElement<MobileCardProps>).props
          .className,
      ),
      onClick: (e: React.MouseEvent) => {
        (
          mobileCardElement as React.ReactElement<MobileCardProps>
        ).props.onClick?.(e);
        handleCardClick();
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        (
          mobileCardElement as React.ReactElement<MobileCardProps>
        ).props.onKeyDown?.(e);
        handleKeyDown(e);
      },
    })
  ) : (
    <div
      role="button"
      tabIndex={-1}
      className="ui:cursor-pointer ui:p-3"
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
    >
      {mobileCardElement}
    </div>
  );

  return (
    <div
      className={cn(
        "ui:overflow-hidden ui:rounded ui:border-divider ui:bg-white ui:shadow-[0_2px_14px_0_rgba(201,201,201,0.50)] ui:dark:border ui:dark:bg-background ui:dark:shadow-none",
        cardClassName,
      )}
    >
      {/* Collapsed state - user's custom content */}
      {collapsedContent}

      {/* Expanded state - all columns */}
      <TableExpansionMobile<Row, Group>
        {...rest}
        row={row}
        isOpen={isOpen}
        classes={classes}
        cardIndex={cardIndex}
      />
    </div>
  );
};
